import { Injectable } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { environment } from '../../../../environments/environment.prod';
import { CurrentUserService } from '../user/current-user.service';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private hubConnection: HubConnection | null = null;
  private hubUrl: string = '';
  private reconnectedHandlers: Array<() => void> = [];
  private reconnectedWired = false;

  constructor(private currentUserService: CurrentUserService) {}

  connect(hubUrl: string, url: string) {
    if (this.hubConnection?.state === HubConnectionState.Connected) {
      this.ensureReconnectedHook();
      return this.hubConnection;
    }

    this.hubUrl = `${url}/${hubUrl}`;

    const currentUser = this.currentUserService.GetSession();
    const builder: HubConnectionBuilder = new HubConnectionBuilder();

    this.hubConnection = builder
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => currentUser.token,
        logger: environment.production ? LogLevel.Critical : LogLevel.Trace,
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build();

    this.ensureReconnectedHook();

    this.startConnection().catch((err) =>
      console.error('Error while starting SignalR connection:', err)
    );

    return this.hubConnection;
  }

  private ensureReconnectedHook() {
    if (!this.hubConnection || this.reconnectedWired) return;
    this.reconnectedWired = true;
    this.hubConnection.onreconnected(() => {
      this.reconnectedHandlers.forEach((handler) => handler());
    });
  }

  onReconnected(handler: () => void) {
    this.reconnectedHandlers.push(handler);
  }

  public async startConnection() {
    if (!this.hubConnection) {
      console.error('Hub connection is null');
      return;
    }

    try {
      await this.hubConnection
      .start()
      .then(() => console.log('Successfully connected', this.hubUrl))
      .catch((error) => {
        console.error('Connection failed:', error);
      });
    } catch (error) {
      console.error('Error establishing SignalR connection:', error);
      if (this.hubConnection && this.hubConnection.state === HubConnectionState.Disconnected) {
        setTimeout(() => {
          this.startConnection();
        }, 5000);
      }
    } finally {
    }
  }

  on(
    hubUrl: string,
    procedureName: string,
    baseUrl: string,
    callBack: (...message: any) => void
  ) {
    if (this.hubConnection) {
      this.hubConnection.on(procedureName, callBack);
    }
  }

  off(eventName: string) {
    if (this.hubConnection) {
      this.hubConnection.off(eventName);
    }
  }

  disconnect(): void {
    if (!this.hubConnection) return;

    this.hubConnection
      .stop()
      .then(() => {
        this.hubConnection = null;
      })
      .catch((err) => {
        console.error('[SignalR] Error stopping connection:', err);
      });
  }
}
